import { DisplaySubMetric, InsightMetric } from '@repo/types';
import { calculatePreviewChartContext } from '@repo/utils';
import { Card, Typography } from 'components';
import useGetKitIdParam from 'hooks/useGetKitIdParam';
import { Suspense, useMemo } from 'react';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import useFeature from 'services/features/useFeature';
import useFetchAndGroupCategories from 'services/insights/useFetchAndGroupCategories';
import { getTopPriorityRankMap } from 'views/Results/utils/categoriesAndMetrics';
import SubMetricRow from '../AllMetrics/MetricsCategories/SubMetricRow';
import styles from './SubMetricData.module.css';

// Metrics Science flagged as related to the one on screen, as All Metrics line
// items. The kit's own metric list decides which ids are shown: a related id the
// kit has no result for (other age bin, sampling state, HCP or legacy metric) is
// simply absent from it. Renders nothing when there is nothing to show.
const RelatedMetricsList = ({ relatedIds }: { relatedIds: number[] }) => {
  const kitId = useGetKitIdParam();
  const { categories } = useFetchAndGroupCategories();
  const { data: allActions } = useActionPlanData({ kitId });

  const topPriorityRanks = useMemo(
    () => getTopPriorityRankMap(categories),
    [categories],
  );

  // Rows follow All Metrics order (section, category, evaluation), not the
  // order Science entered the pairs in, so they sit the way the user knows them.
  const relatedMetrics: DisplaySubMetric[] = useMemo(() => {
    const wanted = new Set(relatedIds);
    return categories
      .flatMap(category => category.orderedMetrics)
      .flatMap(metric => metric.orderedSubmetrics)
      .filter(submetric => wanted.has(submetric.sample_health_id));
  }, [categories, relatedIds]);

  if (relatedMetrics.length === 0) return null;

  const hasAnyChart = relatedMetrics.some(
    submetric => calculatePreviewChartContext(submetric).shouldRender,
  );

  return (
    <div className={styles.relatedMetricsSection}>
      <Typography variant='heading-m'>Related metrics</Typography>
      <Card cardBodyClassName={styles.relatedMetricsCardBody}>
        {relatedMetrics.map(submetric => (
          <SubMetricRow
            key={submetric.sample_health_id}
            submetric={submetric}
            allActions={allActions ?? []}
            topPriorityRanks={topPriorityRanks}
            reserveChartSpace={hasAnyChart}
          />
        ))}
      </Card>
    </div>
  );
};

export default function RelatedMetrics({ metric }: { metric: InsightMetric }) {
  const { isFeatureEnabled } = useFeature('related_metrics');
  const relatedIds = metric.related_metric_ids ?? [];

  if (!isFeatureEnabled || relatedIds.length === 0) return null;

  // The kit's metric list loads on its own so the rest of the page never waits on it.
  return (
    <Suspense fallback={null}>
      <RelatedMetricsList relatedIds={relatedIds} />
    </Suspense>
  );
}
